<template>
    <div>
        <div class="loader" v-if="loading"></div>
        <div v-else>
            <div class="card mb-2">
                <div class="card-body">
                    <div v-if="hasLoadedCalendar">
                        <calendar-details :organizationalCalendarModel="organizationalCalendarModel"
                                          :is-locked-for-edit="isLockedForEdit"
                                          @applied="calendarApplied">
                        </calendar-details>
                    </div>
                    <div v-else>
                        <div class="alert alert-warning" role="alert">
                            {{ $t('organizationalCalendar.missing')}}
                        </div>
                    </div>
                </div>
                <div class="card-footer">
                    <div class="btn-group" role="group" aria-label="gr" v-if="!isLockedForEdit">
                        <button type="button" class="btn btn-primary" @click.stop="loadCalendarModels">
                            {{ $t('organizationalCalendar.modelsLoading') }}
                        </button>
                        <button v-if="!hasLoadedCalendar" type="button" class="btn btn-primary" @click.stop="createCalendarModels">
                            {{ $t('buttons.create.btnText') }}
                        </button>
                    </div>
                </div>
            </div>
            <kendo-datasource ref="organizationalCalendarDatasource"
                              :transport="transportSettings"
                              :schema-model-id="'id'"
                              :schema-model-fields="schemaModelFields"
                              :schema-total="'total'"
                              :schema-data="'data'"
                              :page-size='gridOptions.pageable.size'
                              :server-paging="true"
                              :server-sorting="true"
                              :server-filtering="true"
                              :error="dataSourceError"
                              :requestEnd="datasorceRequestEnd">
            </kendo-datasource>

            <kendo-grid ref="organizationalCalendarGrid"
                        data-source-ref="organizationalCalendarDatasource"
                        :navigatable="gridOptions.navigatable"
                        :editable="gridOptions.editable"
                        :filterable="gridOptions.filterable"
                        :sortable="gridOptions.sortable"
                        :pageable="gridOptions.pageable"
                        :reorderable="gridOptions.reorderable"
                        :resizable="gridOptions.resizable"
                        :groupable="gridOptions.groupable"
                        :allow-copy="gridOptions.allowCopy"
                        :column-menu="gridOptions.columnMenu"
                        :selectable="gridOptions.selectable"
                        :scrollable="gridOptions.scrollable"
                        :toolbar="toolbar"
                        :excel="gridOptions.excel"
                        :pdf="gridOptions.pdf">
                <kendo-grid-column :field="'id'"
                                   :filterable="false"
                                   :title="$t('organizationalCalendarItem.gridHeaders.id')"
                                   :hidden="true">
                </kendo-grid-column>
                <kendo-grid-column :width="70"
                                field="btnsColumn"
                                :template="isLockedForEdit ? [] : btnsTemplate"
                                title=" "
                                >
                </kendo-grid-column>
                <kendo-grid-column :field="'categoryModel'"
                                   :title="$t('organizationalCalendarItem.gridHeaders.categoryDisplayName')"
                                   :editor="categoryIdDropDownEditor"
                                   :template="categoryCellTemplate">
                </kendo-grid-column>
                <kendo-grid-column :field="'name'"
                                   :hidden="isEnVersion"
                                   :title="$t('organizationalCalendarItem.gridHeaders.name')">
                </kendo-grid-column>
                <kendo-grid-column :field="'nameEn'"
                                   :hidden="!isEnVersion"
                                   :title="$t('organizationalCalendarItem.gridHeaders.nameEn')">
                </kendo-grid-column>
                <kendo-grid-column :field="'deadline'"
                                   :format="customDateFormat"
                                   :title="$t('organizationalCalendarItem.gridHeaders.deadline')">
                </kendo-grid-column>
                <kendo-grid-column :field="'alertDate'"
                                   :format="customDateFormat"
                                   :title="$t('organizationalCalendarItem.gridHeaders.alertDate')">
                </kendo-grid-column>
                <kendo-grid-column :field="'printDate'"
                                   :format="customDateFormat"
                                   :title="$t('organizationalCalendarItem.gridHeaders.printDate')">
                </kendo-grid-column>
                <kendo-grid-column :field="'expectedResult'"
                                   :title="$t('organizationalCalendarItem.gridHeaders.expectedResult')">
                </kendo-grid-column>
                <kendo-grid-column :field="'taskId'"
                                   :template="taskIdCellTemplate"
                                   :title="$t('organizationalCalendarItem.gridHeaders.applied')">
                </kendo-grid-column>
                <kendo-grid-column :field="'completed'"
                                   :template="completedCellTemplate"
                                   :title="$t('organizationalCalendarItem.gridHeaders.completedTask')">
                </kendo-grid-column>
                <kendo-grid-column :template="completionTimeCellTemplate"
                                   :title="$t('organizationalCalendarItem.gridHeaders.completionTime')">
                </kendo-grid-column>
                <kendo-grid-column :command="isLockedForEdit? [] : command">
                </kendo-grid-column>
            </kendo-grid>
        </div>

        <ModelLoading :eventId="eventId" :show="showModelLoadings" @onClose="onModelLoadingClose"
                      :is-locked-for-edit="isLockedForEdit"
                      @loaded="onLoaded"></ModelLoading>
        <TaskDisplayModal :value="taskDisplayModel" :show="showTaskDisplay"
                          @onClose="onTaskDisplayClose"></TaskDisplayModal>
        <TaskCompleteModal :value="taskCompleteId" :show="showTaskComplete"
                           :is-locked-for-edit="isLockedForEdit"
                           @onClose="onTaskCompleteClose"></TaskCompleteModal>
    </div>
</template>

<script>
import Vue from 'vue';
import $ from 'jquery';
import JSZip from 'jszip'
import {OrganizationalCalendar} from '../../models/models';
import ModelLoading from '../../components/organizationalCalendar/modelLoading.modal';
import CalendarDetails from '../../components/organizationalCalendar/CalendarDetails';
import BtnsTemplate from '../../components/grid/StandardBtnsTemplate';
import TaskDisplayModal from '../../components/tasks/display.modal';
import TaskCompleteModal from '../../components/tasks/complete.modal';
import { mapGetters } from 'vuex'

/*
   Renders the grid in "DejaVu Sans" font family, which is
   declared in kendo.common.css. It also declares the paths to the
   fonts below using <tt>kendo.pdf.defineFont</tt>, because the
   stylesheet is hosted on a different domain.
   */
kendo.pdf.defineFont({
    "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
    "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
    "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
    "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
    "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
});

export default {
    props: {
        eventId: {
            type: Number,
            required: true,
        },
        details: {
            type: Boolean,
            default: false
        },
        isLockedForEdit: {
            type: Boolean,
            default: false
        }
    },
    components: {
        ModelLoading,
        CalendarDetails,
        TaskDisplayModal,
        TaskCompleteModal
    },
    created() {
        window.JSZip = JSZip;
    },
    data() {
        return {
            componentName: "organizationalCalendarManagement",
            componentTitle: this.$t('organizationalCalendar.title'),
            loading: false,
            unauthorized: false,
            showModelLoadings: false,
            showTaskDisplay: false,
            taskDisplayModel: null,
            taskCompleteId: null,
            showTaskComplete: false,
            organizationalCalendarModel: {
                type: OrganizationalCalendar,
                default: {}
            },
            schemaModelFields: {
                id: {name: 'id', type: 'number', editable: false, nullable: false, visible: true},
                name: {
                    name: 'name',
                    type: 'string',
                    editable: true,
                    nullable: false,
                    visible: true,
                    validation: {required: true}
                },
                nameEn: {name: 'nameEn', type: 'string', editable: true, nullable: true, visible: true},
                categoryModel: {
                    defaultValue: {id: 0, text: 'Select...', textEx: 'Select...'}
                    , validation: {
                        required: true, textvalidation: function (input) {
                            if (input.is("[name='categoryModel']")) {
                                let val = Number(input.val());
                                if (isNaN(val) || val === 0) {
                                    input.attr("data-textvalidation-msg", this.$t('validation.requiredField'));
                                    return /^[A-Z]/.test(input.val());
                                }
                            }

                            return true;
                        }.bind(this)
                    }
                },
                benchmarkType: {
                    defaultValue: {id: 0, text: 'Select...', textEx: 'Select...'}
                    , validation: {
                        required: true, textvalidation: function (input) {
                            if (input.is("[name='benchmarkType']")) {
                                let val = Number(input.val());
                                if (isNaN(val) || val === 0) {
                                    input.attr("data-textvalidation-msg", this.$t('validation.requiredField'));
                                    return /^[A-Z]/.test(input.val());
                                }
                            }

                            return true;
                        }.bind(this)
                    },
                    editable: false, nullable: true, visible: false
                },
                deadlineToAfterBenchemarkIdDays: {
                    name: 'deadlineToAfterBenchemarkIdDays',
                    type: 'number',
                    editable: true,
                    nullable: false,
                    visible: true,
                    validation: {min: -365, max: 365, step: 1}
                },
                priorAlertDeadlineInDays: {
                    name: 'priorAlertDeadlineInDays',
                    type: 'number',
                    editable: true,
                    nullable: false,
                    visible: true,
                    validation: {min: -365, max: 365, step: 1}
                },
                expectedResult: {
                    name: 'expectedResult',
                    type: 'string',
                    editable: true,
                    nullable: true,
                    visible: true
                },
                active: {name: 'active', type: 'boolean', editable: false, nullable: false, visible: true},
                deactivated: {
                    name: 'deactivated',
                    type: 'boolean',
                    editable: true,
                    nullable: false,
                    visible: false
                },
                completed: {name: 'completed', type: 'boolean', editable: false, nullable: false, visible: false},
                deadline: {name: 'deadline', type: 'date', editable: true, nullable: true, visible: true},
                alertDate: {name: 'alertDate', type: 'date', editable: true, nullable: true, visible: true},
                printDate: {name: 'printDate', type: 'date', editable: true, nullable: true, visible: true},
                taskId: {name: 'taskId', type: 'number', editable: false, nullable: true, visible: false},
                hoursToFinish: {
                    name: 'hoursToFinish',
                    type: 'number',
                    editable: false,
                    nullable: true,
                    visible: false
                },
                minutesToFinish: {
                    name: 'minutesToFinish',
                    type: 'number',
                    editable: false,
                    nullable: true,
                    visible: false
                },
                taskCreated: {
                    name: 'taskCreated',
                    type: 'boolean',
                    editable: false,
                    nullable: true,
                    visible: false
                },
                taskStarted: {
                    name: 'taskStarted',
                    type: 'boolean',
                    editable: false,
                    nullable: true,
                    visible: false
                },
                benchmarkTypeId: { name: 'benchmarkTypeId', type: 'number', editable: false, nullable: true, visible: true },

            },
            transportSettings: {
                read: {
                    url: this.$store.getters.baseUrl + '/api/organizationalCalendar/listItems',
                    dataType: 'json',
                    type: 'POST',
                    contentType: 'application/json',
                    beforeSend: this.beforeSend
                },
                create: {
                    url: this.$store.getters.baseUrl + '/api/organizationalCalendar/createItem',
                    dataType: 'json',
                    type: 'POST',
                    contentType: 'application/json',
                    beforeSend: this.beforeSend,
                },
                update: {
                    url: this.$store.getters.baseUrl + '/api/organizationalCalendar/updateItem',
                    dataType: 'json',
                    type: 'PUT',
                    contentType: 'application/json',
                    beforeSend: this.beforeSend
                },
                parameterMap: this.parameterMap
            },
            gridOptions: {
                ...this.$store.getters.gridOptions,
                editable: {
                    mode: 'popup', /*inline, popup*/
                    window: {
                        title: this.$t('organizationalCalendarItem.title'),
                        animation: true
                    },
                    move: false
                },
                excel: {
                    fileName: 'OrganizationalCalendarItems.xlsx',
                    allPages: false,
                    filterable: true,
                    collapsible: true
                },
                pdf: {
                    fileName: 'OrganizationalCalendarItems.pdf',
                    title: this.$t(this.titleKey),
                    subject: this.$t(this.titleKey),
                    creator: this.$store.userName,
                    allPages: false,
                    avoidLinks: true,
                    paperSize: 'A4',
                    margin: {top: '2cm', left: '1cm', right: '1cm', bottom: '1cm'},
                    landscape: true,
                    repeatHeaders: true,
                    scale: 0.6
                }
            }
        }
    },
    methods: {
        beforeSend: function (req) {
            if (this.authToken) {
                req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
            }
        },
        dataSourceError(e) {
            this.$vs.notify({
                title: this.$t('notifications.error'),
                text: this.$helpers.kendoDataSourceErrorText(e),
                color: 'danger',
                time: this.$store.getters.notificationDuration,
            })
        },
        datasorceRequestEnd(e) {
            if (e.type == "create" && e.response) {
                e.sender.read()

                let statusCode = this.$helpers.errorStatusCode(e);
                this.$vs.notify({
                    title: this.$t('request.response.status.' + statusCode),
                    text: this.$helpers.errorText(e.response),
                    color: statusCode === 200 ? 'success' : 'danger',
                    time: this.$store.getters.notificationDuration
                })
            } else if (e.type == "update" && e.response) {
                e.sender.read()

                let statusCode = this.$helpers.errorStatusCode(e);
                this.$vs.notify({
                    title: this.$t('request.response.status.' + statusCode),
                    text: this.$helpers.errorText(e.response),
                    color: statusCode === 200 ? 'success' : 'danger',
                    time: this.$store.getters.notificationDuration
                })
            } else if (e.type == "destroy" && e.response) {
                e.sender.read()

                let statusCode = this.$helpers.errorStatusCode(e);
                this.$vs.notify({
                    title: this.$t('request.response.status.' + statusCode),
                    text: this.$helpers.errorText(e.response),
                    color: statusCode === 200 ? 'success' : 'danger',
                    time: this.$store.getters.notificationDuration
                })
            }
        },
        parameterMap: function (options, operation) {
            if (operation !== 'read' && options.models) {
                return {models: kendo.stringify(options.models)}
            } else {
                let newOptions = {
                    id: this.organizationalCalendarModel.id,
                    eventId: this.eventId,
                    request: options,
                    model: options
                };
                return kendo.stringify(newOptions);
            }
        },
        accessDenied() {
            this.unauthorized = true;
        },
        activeCellTemplate: function (dataItem) {
            let templateHtml =
                '<span class="badge ' + (dataItem.active ? 'badge-success' : 'badge-danger') + '">' + kendo.htmlEncode(dataItem.active ? this.$t('yes') : this.$t('no')) + '</span>';

            return templateHtml;
        },
        categoryCellTemplate: function (dataItem) {
            if (dataItem.categoryModel === null) {
                return kendo.htmlEncode("");
            }

            if (dataItem.categoryModel.text === null && dataItem.categoryModel.textEn === null) {
                return kendo.htmlEncode("");
            }

            let templateHtml = '<span>' + kendo.htmlEncode(this.isEnVersion ? dataItem.categoryModel.textEn : dataItem.categoryModel.text) + '</span>';

            return templateHtml;
        },
        categoryIdDropDownEditor: function (container, options) {
            $('<input name="' + options.field + '"/> id="' + options.field + '"')
                .appendTo(container)
                .kendoDropDownList({
                    autoBind: false,
                    dataTextField: this.isEnVersion ? 'textEn' : 'text',
                    dataValueField: "id",
                    optionLabel: this.$t('selectCategory'),
                    dataSource: {
                        transport: {
                            read: {
                                url: this.$store.getters.baseUrl + '/api/organizationalModel/categoryDropdownSource',
                                dataType: 'json',
                                type: 'GET',
                                contentType: 'application/json',
                                beforeSend: this.beforeSend
                            },
                        }
                    }
                });
        },
        benchmarkTypeCellTemplate: function (dataItem) {
            if (dataItem.benchmarkType === null) {
                return kendo.htmlEncode("");
            }

            if (dataItem.benchmarkType.text === null && dataItem.benchmarkType.textEn === null) {
                return kendo.htmlEncode("");
            }

            let templateHtml = '<span>' + kendo.htmlEncode(this.isEnVersion ? dataItem.benchmarkType.textEn : dataItem.benchmarkType.text) + '</span>';

            return templateHtml;
        },
        benchmarkTypeIdDropDownEditor: function (container, options) {
            $('<input name="' + options.field + '"/> id="' + options.field + '"')
                .appendTo(container)
                .kendoDropDownList({
                    autoBind: false,
                    dataTextField: this.isEnVersion ? 'textEn' : 'text',
                    dataValueField: "id",
                    optionLabel: this.$t('selectBenchmark'),
                    dataSource: {
                        transport: {
                            read: {
                                url: this.$store.getters.baseUrl + '/api/organizationalModel/benchmarkTypeDropdownSource',
                                dataType: 'json',
                                type: 'GET',
                                contentType: 'application/json',
                                beforeSend: this.beforeSend
                            },
                        }
                    }
                });
        },
        loadOrganizationalCalendar() {
            this.loading = true;

            this.$http.get(`/api/organizationalCalendar/getActiveForEvent/${this.eventId}`)
                .then(response => {
                    if (response.data) {
                        this.organizationalCalendarModel = new OrganizationalCalendar(response.data);
                    }

                })
                .catch(error => {
                    this.$vs.notify({
                        title: this.$t('buttons.load.btnText'),
                        text: this.$helpers.errorText(error),
                        color: this.$helpers.errorColor(error),
                        time: this.$store.getters.notificationDuration
                    });
                })
                .then(() => { this.loading = false; });
        },
        taskIdCellTemplate(dataItem) {
            let taskId = Number(dataItem.taskId);
            let templateHtml = `<span class="badge ${isNaN(taskId) || taskId === 0 ? 'badge-secondary' : 'badge-success'}">` + kendo.htmlEncode(isNaN(taskId) || taskId === 0 ? this.$t('no') : this.$t('yes')) + '</span>';

            return templateHtml;
        },
        completedCellTemplate(dataItem) {
            let templateHtml = `<span class="badge ${dataItem.completed ? 'badge-success' : 'badge-warning'}">` + kendo.htmlEncode(dataItem.completed ? this.$t('yes') : this.$t('no')) + '</span>';

            return templateHtml;
        },
        completionTimeCellTemplate(dataItem) {
            if (!dataItem.completed) {
                return kendo.htmlEncode("");
            }

            let hoursToFinish = Number(dataItem.hoursToFinish);
            let minutesToFinish = Number(dataItem.minutesToFinish);
            let template = "";
            if (!isNaN(hoursToFinish)) {
                template += `${dataItem.hoursToFinish}h`
            }

            if (!isNaN(minutesToFinish)) {
                template += ` and ${dataItem.minutesToFinish}min`
            }
            return kendo.htmlEncode(template);
        },
        createCalendarModels() {
            this.loading = true;
            const model = { eventId: this.eventId };

            this.$http.post(`/api/organizationalCalendar/createOrUpdate/`, model)
                .then(() => {
                    this.loadOrganizationalCalendar();
                })
                .catch(error => {
                    this.$vs.notify({
                        title: this.$t('buttons.create.btnText'),
                        text: this.$helpers.errorText(error),
                        color: this.$helpers.errorColor(error),
                        time: this.$store.getters.notificationDuration
                    });
                })
                .then(() => { this.loading = false; });
        },
        loadCalendarModels() {
            this.showModelLoadings = true;
        },
        onModelLoadingClose() {
            this.showModelLoadings = false;
        },
        onLoaded() {
            this.onModelLoadingClose();

            if (!this.hasLoadedCalendar) {
                this.loadOrganizationalCalendar();
            }
            this.refreshGrid();
        },
        refreshGrid() {
            let grid = this.$refs.organizationalCalendarGrid.kendoWidget();
            if (grid) {
                grid.dataSource.read();
            }
        },
        btnsTemplate: function (e) {
            return {
                template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                templateArgs: {
                    ...e,
                    btns: [
                        {
                            name: 'apply',
                            text: '',
                            tooltip: this.$t('buttons.apply.tooltipText'),
                            class: 'btn btn-success',
                            icon: 'fas fa-tasks',
                            show: this.hasEditRights,
                            clickHandler: this.applyCalendarItem
                        },
                        {
                            name: 'taskDetails',
                            text: '',
                            tooltip: this.$t('buttons.taskDetails.tooltipText'),
                            class: 'btn btn-info',
                            icon: 'fas fa-eye',
                            show: this.hasReadRights && e.taskId > 0,
                            clickHandler: this.showTaskDetailsModal
                        },
                        {
                            name: 'complete',
                            text: '',
                            tooltip: this.$t('organizationalCalendarItem.completeTask'),
                            class: 'btn btn-success',
                            icon: 'fas fa-check',
                            show: e.completed === false && e.taskId > 0 && e.taskStarted === true,
                            clickHandler: this.completeTask
                        },
                        {
                            name: 'delete',
                            text: '',
                            tooltip: this.$t('buttons.delete.tooltipText'),
                            class: 'btn btn-danger',
                            icon: 'fas fa-trash',
                            show: this.hasEditRights,
                            clickHandler: this.deleteCalendarItem
                        },

                    ]
                },
            };
        },
        applyCalendarItem(dataItem) {
            if (!this.hasEditRights) {
                this.$vs.notify({
                    title: this.$t('buttons.apply.action'),
                    text: this.$t('insufficientRights'),
                    color: 'warning',
                    time: this.$store.getters.notificationDuration
                })

                return;
            }

            if (!dataItem.id) {
                return;
            }

            kendo.confirm(`${this.$t('organizationalCalendarItem.applyConfirmation')} '${dataItem.name}'?`)
                .then(function () {
                    this.$http.post('/api/organizationalCalendar/applyItem', dataItem).then((response => {
                        this.$vs.notify({
                            title: this.$t('buttons.apply.action'),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });

                        this.refreshGrid();
                    })).catch(error => {
                        console.log(error);
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
                }.bind(this));
        },
        showTaskDetailsModal(dataItem) {
            if (!this.hasReadRights) {
                this.$vs.notify({
                    title: this.$t('buttons.details.action'),
                    text: this.$t('insufficientRights'),
                    color: 'warning',
                    time: this.$store.getters.notificationDuration
                })

                return
            }

            if (!dataItem.id || !dataItem.taskId) {
                return;
            }

            this.loadTaskModel(dataItem.taskId);
        },
        deleteCalendarItem(dataItem) {
            if (!this.hasEditRights) {
                this.$vs.notify({
                    title: this.$t('buttons.delete.action'),
                    text: this.$t('insufficientRights'),
                    color: 'warning',
                    time: this.$store.getters.notificationDuration
                })

                return
            }

            if (!dataItem.id) {
                return;
            }

            kendo.confirm(`${this.$t('organizationalCalendarItem.deleteConfirmation')} '${dataItem.name}'?`)
                .then(function () {
                    this.$http.delete('/api/organizationalCalendar/DeleteItem', {params: {id: dataItem.id}}).then((response => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                            text: this.$helpers.responseMessage(response),
                            color: 'success',
                            time: this.$store.getters.notificationDuration
                        });

                        this.refreshGrid();
                    })).catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
                }.bind(this));
        },
        completeTask(e) {
            if (!this.hasEditRights) {
                this.$vs.notify({
                    title: this.$t('buttons.apply.action'),
                    text: this.$t('insufficientRights'),
                    color: 'warning',
                    time: this.$store.getters.notificationDuration
                })

                return
            }

            if (e.completed) {
                this.$vs.notify({
                    title: this.$t('tasks.activities.completed'),
                    text: this.$t('organizationalCalendarItem.taskAlreadyCompleted'),
                    color: 'warning',
                    time: this.$store.getters.notificationDuration
                })

                return
            }

            this.taskCompleteId = e.taskId;
            this.showTaskComplete = true;
        },
        onTaskDisplayClose() {
            this.showTaskDisplay = false;
            this.taskDisplayModel = null;
        },
        onTaskCompleteClose() {
            this.taskCompleteId = null;
            this.showTaskComplete = false;
            this.refreshGrid();
        },
        loadTaskModel(id) {
            this.$http.get(`/api/tasks/${id}`).then(result => {
                if (result.data) {
                    this.taskDisplayModel = result.data;
                    this.showTaskDisplay = true;
                }

            }).catch(error => {
                this.$vs.notify({
                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                    text: this.$helpers.errorText(error),
                    color: this.$helpers.errorColor(error),
                    time: this.$store.getters.notificationDuration
                });
            });
        },
        calendarApplied() {
            this.refreshGrid();
        }
    },
    beforeMount() {
        this.loadOrganizationalCalendar();
    },
    mounted() {

    },
    computed: {
        ...mapGetters(['isEnVersion', 'dateFormat', 'dateTimeFormat', 'authToken']),
        customDateFormat() {
            return `{0:${this.dateFormat}}`;
        },
        hasEditRights() {
            return true;
        },
        toolbar: function () {
            return this.isLockedForEdit || !this.hasLoadedCalendar
                ? [ { name: 'pdf', text: this.$t('buttons.pdfExport.tooltipText') },
                    { name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
                : [ { name: 'create', text: this.$t('buttons.newTask.btnText') },
                    { name: 'pdf', text: this.$t('buttons.pdfExport.tooltipText') },
                    { name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
        },
        command: function () {
            return [{name: 'edit', text: this.$t('buttons.edit.btnText')}]
        },
        group() {
            return {};

            // return this.isEnVersion ? { field: 'categoryDisplayNameEn' } : { field: 'categoryDisplayName' };
        },
        hasLoadedCalendar() {
            let id = Number(this.organizationalCalendarModel.id);
            return !isNaN(id);
        }
    }
}
</script>

<style scoped>
/*
    Use the DejaVu Sans font for display and embedding in the PDF file.
    The standard PDF fonts have no support for Unicode characters.
*/
.k-pdf-export .k-grid {
    font-family: "DejaVu Sans", "Arial", sans-serif;
}

.k-pdf-export .k-filterable .k-grid-filter {
    display: none;
}

.k-pdf-export .k-with-icon .k-header-column-menu {
    display: none;
}

.k-grid .k-grid-header .k-header .k-link {
    height: auto;
}

.k-grid .k-grid-header .k-header {
    white-space: normal;
}

.k-grid-edit, .k-grid-details, .k-grid-delete, .k-grid-pdf, .k-grid-excel {
    padding: 0;
    margin-bottom: 2px;
}

</style>
